import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import type { Trip, TripPaymentStatus } from "@shared/schema";
import NewTripFields, { type NewTripData } from "@/components/invoicing/NewTripFields";
import type { DestinationOption } from "@/components/DestinationSelector";
import { useUpdateTrip } from "@/hooks/useInvoices";
import { useToast } from "@/hooks/use-toast";
import { TripPaymentStatusBadge } from "@/components/ui/TripPaymentStatusBadge";
import { useResolvedFeatureFlags } from "@/hooks/useResolvedFeatureFlags";
import { FINANCES_PATHS } from "@/lib/financesPaths";

function formatDateAsLocal(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const str = typeof value === "string" ? value : value.toISOString();
  const datePart = str.substring(0, 10);
  return format(new Date(`${datePart}T00:00:00`), "MM/dd/yyyy");
}

interface TripDetailsCardProps {
  trip: Trip;
  canEdit: boolean;
  paymentStatus?: TripPaymentStatus;
  onDirtyChange?: (dirty: boolean) => void;
}

function tripToFormData(
  trip: Trip,
  destinations: DestinationOption[],
): NewTripData {
  return {
    name: trip.name,
    destinations,
    isGroup: !!trip.isGroup,
    groupName: trip.groupName || "",
  };
}

function cloneTripFormData(data: NewTripData): NewTripData {
  return structuredClone(data);
}

export default function TripDetailsCard({
  trip,
  canEdit,
  paymentStatus,
  onDirtyChange,
}: TripDetailsCardProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const updateTrip = useUpdateTrip();
  const { isEnabled } = useResolvedFeatureFlags();
  const invoicingMvpEnabled = isEnabled("invoicingMvp");
  const destinationsQueryKey = [`/api/destinations/trip/${trip.id}`] as const;

  const { data: tripDestinations = [], isSuccess: destinationsLoaded } = useQuery<DestinationOption[]>({
    queryKey: destinationsQueryKey,
    enabled: invoicingMvpEnabled,
    queryFn: async () => {
      const res = await fetch(`/api/destinations/trip/${trip.id}`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      const rows = await res.json();
      return rows.map((r: { worldviaDestinationId: string; displayName: string; destinationType: DestinationOption["destinationType"]; parentRegion?: string | null }) => ({
        id: r.worldviaDestinationId,
        displayName: r.displayName,
        destinationType: r.destinationType,
        parentRegion: r.parentRegion ?? null,
      }));
    },
  });

  const [form, setForm] = useState<NewTripData>(() =>
    tripToFormData(trip, []),
  );
  const [savedForm, setSavedForm] = useState<NewTripData | null>(null);
  const hydratedTripIdRef = useRef<number | null>(null);

  // Hydrate form from server once per trip when destinations finish loading.
  // Do not re-hydrate after save — that was overwriting in-progress edits.
  useEffect(() => {
    if (hydratedTripIdRef.current === trip.id) return;

    if (!invoicingMvpEnabled) {
      const next = tripToFormData(trip, []);
      setForm(cloneTripFormData(next));
      setSavedForm(cloneTripFormData(next));
      hydratedTripIdRef.current = trip.id;
      return;
    }

    if (!destinationsLoaded) return;

    const next = tripToFormData(trip, tripDestinations);
    setForm(cloneTripFormData(next));
    setSavedForm(cloneTripFormData(next));
    hydratedTripIdRef.current = trip.id;
  }, [trip, tripDestinations, destinationsLoaded, invoicingMvpEnabled]);

  const lastDirtyRef = useRef<boolean | null>(null);
  useEffect(() => {
    const dirty =
      canEdit &&
      savedForm != null &&
      JSON.stringify(form) !== JSON.stringify(savedForm);
    if (lastDirtyRef.current === dirty) return;
    lastDirtyRef.current = dirty;
    onDirtyChange?.(dirty);
  }, [form, savedForm, canEdit, onDirtyChange]);

  const handleSave = async () => {
    const snapshot: NewTripData = {
      name: form.name.trim(),
      destinations: [...form.destinations],
      isGroup: form.isGroup,
      groupName: form.groupName,
    };

    if (!snapshot.name) {
      toast({
        title: "Trip name required",
        variant: "destructive",
      });
      return;
    }
    if (invoicingMvpEnabled && snapshot.destinations.length === 0) {
      toast({
        title: "At least one destination is required",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateTrip.mutateAsync({
        id: trip.id,
        data: {
          name: snapshot.name,
          isGroup: snapshot.isGroup,
          groupName: snapshot.isGroup
            ? snapshot.groupName?.trim() || undefined
            : null,
          ...(invoicingMvpEnabled
            ? {
                destinations: snapshot.destinations.map((d) => ({
                  id: d.id,
                  displayName: d.displayName,
                  destinationType: d.destinationType,
                  parentRegion: d.parentRegion ?? null,
                })),
              }
            : {}),
        },
      });

      setForm(cloneTripFormData(snapshot));
      setSavedForm(cloneTripFormData(snapshot));
      queryClient.setQueryData(destinationsQueryKey, snapshot.destinations);
      await queryClient.invalidateQueries({ queryKey: destinationsQueryKey });

      toast({ title: "Trip updated" });
      setLocation(FINANCES_PATHS.invoices);
    } catch (error: unknown) {
      toast({
        title: "Failed to update trip",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const startDate = formatDateAsLocal(trip.startDate);
  const endDate = formatDateAsLocal(trip.endDate);

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base">Trip Details</CardTitle>
          {paymentStatus && <TripPaymentStatusBadge status={paymentStatus} />}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit ? (
          <NewTripFields
            value={form}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            idPrefix={`edit-trip-${trip.id}`}
          />
        ) : (
          <div className="space-y-3 rounded-md border p-4 bg-muted/20">
            <div>
              <p className="text-xs text-muted-foreground">Trip Name</p>
              <p className="font-medium">{trip.name}</p>
            </div>
            {(form.destinations.length > 0 || tripDestinations.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {(form.destinations.length > 0 ? form.destinations : tripDestinations).map((d) => (
                  <Badge key={d.id} variant="secondary">{d.displayName}</Badge>
                ))}
              </div>
            )}
            {trip.isGroup && trip.groupName && (
              <p className="text-sm text-muted-foreground">Group Booking Number: {trip.groupName}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Start Date</p>
            <p className="text-sm font-medium">{startDate}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">End Date</p>
            <p className="text-sm font-medium">{endDate}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Trip dates are derived from this trip&apos;s invoices and components.
        </p>

        {(trip.recordLocator) && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {trip.recordLocator && (
              <Badge variant="outline">Trip ID: {trip.recordLocator}</Badge>
            )}
          </div>
        )}

        {canEdit && (
          <Button
            className="w-full"
            onClick={handleSave}
            disabled={updateTrip.isPending}
          >
            {updateTrip.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Trip"
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
